import { useRecoilValue } from 'recoil';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { EnergyDataTrend } from '@demind-inc/core';

import {
  dataTrendPeriodAtom,
  getWeekDatesOf,
  selectedDateAtom,
  useAuthContext,
  useEnergyDailyTrend,
  useEnergyWeeklyTrend,
} from '../data-access';

export const useEnergyTrend = () => {
  const dataTrendPeriod = useRecoilValue(dataTrendPeriodAtom);
  const selectedDate = useRecoilValue(selectedDateAtom);
  const { user } = useAuthContext();

  const { energyTrend: energyDailyTrend, isLoading: isDailyLoading } = useEnergyDailyTrend({
    dataTrendId: user.dataTrendId!,
    date: selectedDate.format('YYYY-MM-DD'),
    enabled: dataTrendPeriod === 'daily',
  });

  const { energyTrend: prevDayEnergyTrend } = useEnergyDailyTrend({
    dataTrendId: user.dataTrendId!,
    date: selectedDate.subtract(1, 'day').format('YYYY-MM-DD'),
    enabled: dataTrendPeriod === 'daily',
  });

  const peakEnergy = energyDailyTrend?.peakEnergy ?? 0;
  const prevPeakEnergy = prevDayEnergyTrend?.peakEnergy ?? null;
  const peakEnergyDiff = prevPeakEnergy !== null ? Math.round(peakEnergy - prevPeakEnergy) : null;
  const peakEnergyDiffColor =
    peakEnergyDiff !== null
      ? peakEnergyDiff > 0
        ? 'var(--color-green-base)'
        : peakEnergyDiff < 0
          ? 'var(--color-red-base)'
          : peakEnergyDiff === 0
            ? 'var(--color-gray-dark)'
            : 'var(--color-black)'
      : undefined;

  const weekEdge = useMemo(() => {
    const week = getWeekDatesOf(selectedDate);
    return {
      startDate: week[0],
      endDate: week[week.length - 1],
    };
  }, [selectedDate]);

  const prevWeekEdge = useMemo(() => {
    const prevWeekStart = weekEdge.startDate.subtract(7, 'day');
    const prevWeekEnd = weekEdge.endDate.subtract(7, 'day');
    return {
      startDate: prevWeekStart,
      endDate: prevWeekEnd,
    };
  }, [weekEdge]);

  const { energyTrend: energyWeeklyTrend, isLoading: isWeeklyLoading } = useEnergyWeeklyTrend({
    dataTrendId: user.dataTrendId!,
    startDate: weekEdge.startDate.format('YYYY-MM-DD'),
    endDate: weekEdge.endDate.format('YYYY-MM-DD'),
    enabled: dataTrendPeriod === 'weekly',
  });

  const { energyTrend: prevWeekEnergyTrend } = useEnergyWeeklyTrend({
    dataTrendId: user.dataTrendId!,
    startDate: prevWeekEdge.startDate.format('YYYY-MM-DD'),
    endDate: prevWeekEdge.endDate.format('YYYY-MM-DD'),
    enabled: dataTrendPeriod === 'weekly',
  });

  const avgPeakEnergy = energyWeeklyTrend?.avgPeakEnergy ?? 0;
  const prevAvgPeakEnergy = prevWeekEnergyTrend?.avgPeakEnergy ?? null;
  const avgPeakEnergyDiff =
    prevAvgPeakEnergy !== null ? Math.round(avgPeakEnergy - prevAvgPeakEnergy) : null;
  const avgPeakEnergyDiffColor =
    avgPeakEnergyDiff !== null
      ? avgPeakEnergyDiff > 0
        ? 'var(--color-green-base)'
        : avgPeakEnergyDiff < 0
          ? 'var(--color-red-base)'
          : 'var(--color-black)'
      : undefined;

  const highEnergyDuration = energyDailyTrend?.highEnergyDurationMin ?? 0;
  const prevHighEnergyDuration = prevDayEnergyTrend?.highEnergyDurationMin ?? null;
  const highEnergyDurationDiff =
    prevHighEnergyDuration !== null ? highEnergyDuration - prevHighEnergyDuration : null;
  const highEnergyDurationDiffColor =
    highEnergyDurationDiff !== null
      ? highEnergyDurationDiff > 0
        ? 'var(--color-green-base)'
        : highEnergyDurationDiff < 0
          ? 'var(--color-red-base)'
          : highEnergyDurationDiff === 0
            ? 'var(--color-gray-dark)'
            : 'var(--color-black)'
      : undefined;

  const weeklyHighEnergyDuration = useMemo(() => {
    if (!energyWeeklyTrend?.data) return 0;
    const weeklyData = energyWeeklyTrend.data as EnergyDataTrend[];
    const totalDuration = weeklyData.reduce((sum, day) => {
      return sum + (day.highEnergyDurationMin ?? 0);
    }, 0);
    return Math.round(totalDuration / weeklyData.length);
  }, [energyWeeklyTrend?.data]);

  const prevWeeklyHighEnergyDuration = useMemo(() => {
    if (!prevWeekEnergyTrend?.data) return null;
    const prevWeeklyData = prevWeekEnergyTrend.data as EnergyDataTrend[];
    const totalDuration = prevWeeklyData.reduce((sum, day) => {
      return sum + (day.highEnergyDurationMin ?? 0);
    }, 0);
    return Math.round(totalDuration / prevWeeklyData.length);
  }, [prevWeekEnergyTrend?.data]);

  const weeklyHighEnergyDurationDiff =
    prevWeeklyHighEnergyDuration !== null
      ? weeklyHighEnergyDuration - prevWeeklyHighEnergyDuration
      : null;
  const weeklyHighEnergyDurationDiffColor =
    weeklyHighEnergyDurationDiff !== null
      ? weeklyHighEnergyDurationDiff > 0
        ? 'var(--color-green-base)'
        : weeklyHighEnergyDurationDiff < 0
          ? 'var(--color-red-base)'
          : weeklyHighEnergyDurationDiff === 0
            ? 'var(--color-gray-dark)'
            : 'var(--color-black)'
      : undefined;

  const isLoading = dataTrendPeriod === 'daily' ? isDailyLoading : isWeeklyLoading;

  return {
    dataTrendPeriod,
    isLoading,
    energyDailyTrend,
    energyWeeklyTrend,
    prevDayEnergyTrend,
    peakEnergyDiff,
    peakEnergyDiffColor,
    prevWeekEnergyTrend,
    avgPeakEnergyDiff,
    avgPeakEnergyDiffColor,
    highEnergyDurationDiff,
    highEnergyDurationDiffColor,
    weeklyHighEnergyDuration,
    weeklyHighEnergyDurationDiff,
    weeklyHighEnergyDurationDiffColor,
  };
};
